import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { GoogleReCaptchaProvider, withGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useConfig } from 'config/ConfigContext';
import cn from 'classnames';
import { get } from 'lodash';
import validate from 'web-form-validator';
import { toast } from 'react-toastify';

import Input from 'components/Form/Input';
import Validator from 'components/Validator/Validator';
import { Button } from 'components/Button';
import { submitBorrowerSelfIdentificationAction } from 'actions/quickpay';

import styles from './styles.module.scss';
import borrowerSelfIdentificationSchema from './schemas/borrowerSelfIdentificationSchema';

const initialValues = {
  email: '',
  phone: '',
  ssnLast4: '',
};

const toastError = (message) => {
  toast.error(message, { position: 'top-center' });
};

const resolveRequestErrorMessage = (err) => (
  get(err, 'data.message')
  || get(err, 'data.errorMessages[0]')
  || get(err, 'message')
  || 'An error occurred. Please try again.'
);

const appendIntentToLink = (link, intent) => {
  if (!intent || typeof link !== 'string' || /^https?:\/\//i.test(link)) {
    return link;
  }

  const [pathWithQuery, hash = ''] = link.split('#');
  const [pathname = '', queryString = ''] = pathWithQuery.split('?');
  const params = new URLSearchParams(queryString);
  if (!params.has('intent')) {
    params.set('intent', intent);
  }
  const query = params.toString();
  const hashPart = hash ? `#${hash}` : '';
  return query ? `${pathname}?${query}${hashPart}` : `${pathname}${hashPart}`;
};

const BorrowerSelfIdentification = (props) => {
  const { uid: uidFromPath } = useParams();
  const [searchParams] = useSearchParams();
  const uid = uidFromPath || searchParams.get('uid');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  const {
    validator: {
      values,
      errors,
      onChangeHandler,
      replaceErrors,
    },
  } = props;

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'ssnLast4') {
      const digits = value.replace(/\D/g, '').slice(0, 4);
      onChangeHandler(name, digits);
      return;
    }
    if (name === 'phone') {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      onChangeHandler(name, digits);
      return;
    }
    onChangeHandler(name, value);
  };

  const resolveRedirectLink = (data) => (
    get(data, 'url')
    || get(data, 'link')
    || get(data, 'redirectUrl')
    || get(data, 'redirect')
  );

  const submitIdentification = (recaptchaToken) => {
    const emailTrim = typeof values.email === 'string' ? values.email.trim() : '';
    const phoneDigits = String(values.phone || '').replace(/\D/g, '');
    const ssnDigits = String(values.ssnLast4 || '').replace(/\D/g, '');

    const body = {
      ssnLast4: ssnDigits,
      token: recaptchaToken,
    };
    if (emailTrim) {
      body.email = emailTrim;
    }
    if (phoneDigits.length === 10) {
      body.phone = phoneDigits;
    }

    const intent = searchParams.get('intent');
    const params = {};
    if (intent) {
      params.intent = intent;
    }

    dispatch(submitBorrowerSelfIdentificationAction({
      uid,
      data: body,
      params,
      success: (res) => {
        setIsLoading(false);
        const link = resolveRedirectLink(res);
        if (!link || typeof link !== 'string') {
          toastError('We could not continue. Please try again or contact support.');
          return;
        }

        const nextLink = appendIntentToLink(link, intent);
        if (/^https?:\/\//i.test(link)) {
          window.location.assign(link);
        } else {
          navigate(nextLink);
        }
      },
      fail: (err) => {
        setIsLoading(false);
        toastError(resolveRequestErrorMessage(err));
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setAttemptedSubmit(true);

    const {
      googleReCaptchaProps: { executeRecaptcha },
    } = props;

    const { errors: schemaErrors } = validate(borrowerSelfIdentificationSchema, values);
    replaceErrors(schemaErrors);

    if (Object.keys(schemaErrors).length > 0) {
      return;
    }

    setIsLoading(true);

    executeRecaptcha('quickpay_campaign_identify').then((recaptchaToken) => {
      submitIdentification(recaptchaToken);
    });
  };

  return (
    <div className={cn(styles.quickpay, styles.borrowerSelfId)}>
      <form className={styles.content} onSubmit={handleSubmit} noValidate>
        <header>
          <h1 className={styles.title}>Verify your identity</h1>
          <p className={styles.subtitle}>
            Enter the email or phone number on your loan account and the last 4 digits of your SSN.
          </p>
        </header>

        <div className={styles.fields}>
          <Input
            label="Email"
            name="email"
            type="email"
            value={values.email}
            onChange={handleInputChange}
            hasError={attemptedSubmit && !!errors.email}
            errorMessage={attemptedSubmit ? errors.email : ''}
            isBadgeVisible={false}
            isDisabled={false}
            readOnly={false}
            placeHolder="you@example.com"
            autoComplete="email"
            labelClassName={styles.fieldLabel}
          />

          <div className={styles.orDivider} aria-hidden="true">
            <span>OR</span>
          </div>

          <Input
            label="Phone number"
            name="phone"
            type="tel"
            value={values.phone}
            onChange={handleInputChange}
            hasError={attemptedSubmit && !!errors.phone}
            errorMessage={attemptedSubmit ? errors.phone : ''}
            isBadgeVisible={false}
            isDisabled={false}
            readOnly={false}
            inputMode="numeric"
            maxLength={10}
            placeHolder="(555) 000-0000"
            autoComplete="tel-national"
            labelClassName={styles.fieldLabel}
          />

          <div className={styles.sectionDivider} aria-hidden="true" />

          <Input
            label="Last 4 digits of SSN"
            name="ssnLast4"
            value={values.ssnLast4}
            onChange={handleInputChange}
            hasError={attemptedSubmit && !!errors.ssnLast4}
            errorMessage={attemptedSubmit ? errors.ssnLast4 : ''}
            isBadgeVisible={false}
            isDisabled={false}
            readOnly={false}
            inputMode="numeric"
            maxLength={4}
            placeHolder="••••"
            autoComplete="off"
            labelClassName={styles.fieldLabel}
          />
        </div>

        <Button
          type="submit"
          color="primary"
          className={cn('w-100', styles.submit, 'mt-4')}
          disabled={isLoading}
        >
          {isLoading ? 'Please wait…' : 'Continue'}
        </Button>

        <p className={styles.recaptchaNotice}>
          This site is protected by reCAPTCHA and the Google&nbsp;
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          {' '}
          and&nbsp;
          <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          &nbsp;also apply.
        </p>
      </form>
    </div>
  );
};

BorrowerSelfIdentification.propTypes = {
  validator: PropTypes.object.isRequired,
  googleReCaptchaProps: PropTypes.object.isRequired,
};

const BorrowerSelfIdentificationForm = compose(
  Validator(borrowerSelfIdentificationSchema, initialValues),
  withGoogleReCaptcha
)(BorrowerSelfIdentification);

const BorrowerSelfIdentificationPage = () => {
  const { quickCampaignReCaptchaKey } = useConfig();

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={quickCampaignReCaptchaKey}
      scriptProps={{ id: 'google-recaptcha-v3-quickpay-campaign' }}
    >
      <BorrowerSelfIdentificationForm />
    </GoogleReCaptchaProvider>
  );
};

export default BorrowerSelfIdentificationPage;
